import { canManageTeam, canOperate, STAFF_ROLE_WORDS, type StaffRole } from "@atzmai/schema";
import { createContext, useContext } from "react";
import { Link, NavLink, Outlet } from "react-router";
import { useApi } from "../../lib/useApi.ts";

interface Staff { email: string; role: StaffRole }

const StaffContext = createContext<Staff | null>(null);

/** Who is signed in, for hiding what they can't do. The Worker is what enforces it. */
export function useStaff() {
  const staff = useContext(StaffContext)!;
  return { ...staff, canOperate: canOperate(staff.role) };
}

export const SCREENS = [
  { to: "/console", label: "Overview", question: "Is anything on fire?", end: true },
  { to: "/console/releases", label: "Releases", question: "Is this release safe to widen?" },
  { to: "/console/devices", label: "Devices", question: "Which machines, where, in what state?" },
  { to: "/console/crashes", label: "Crashes", question: "What broke, and who owns it?" },
  { to: "/console/feedback", label: "Feedback", question: "What are people telling us?" },
  { to: "/console/funnel", label: "Funnel", question: "Are we growing, honestly counted?" },
  { to: "/console/approvals", label: "Approvals", question: "Who do we let in?" },
  { to: "/console/team", label: "Team", question: "Who runs this, and how much can they do?", superOnly: true },
];

export function ConsoleLayout() {
  const me = useApi<Staff>("/api/console/me");
  if (!me.data) return <main className="mx-auto max-w-xl p-10"><Loading error={me.error} /></main>;
  const { email, role } = me.data;
  return (
    <StaffContext.Provider value={me.data}>
    <div className="flex min-h-dvh flex-col md:flex-row">
      <aside className="flex shrink-0 gap-1 overflow-x-auto border-b border-line bg-orbit-2 p-3 md:sticky md:top-0 md:h-dvh md:w-60 md:flex-col md:border-r md:border-b-0">
        <Link to="/console" className="mb-0 flex items-center gap-3 px-2 py-2 font-display text-lg font-medium text-ink no-underline md:mb-5">
          <span className="orb" aria-hidden="true" />
          Console
        </Link>
        {SCREENS.filter((s) => !s.superOnly || canManageTeam(role)).map((s) => (
          <NavLink key={s.to} to={s.to} end={s.end}
            className={({ isActive }) => `whitespace-nowrap rounded-full px-4 py-2 font-display text-[15px] font-medium no-underline ${isActive ? "bg-ink text-orbit" : "text-ink-2 hover:bg-glass hover:text-ink"}`}>
            {s.label}
          </NavLink>
        ))}
        <div className="hidden px-4 pt-4 text-[13px] text-ink-3 md:mt-auto md:block">
          <div className="[overflow-wrap:anywhere]">{email}</div>
          <div>{STAFF_ROLE_WORDS[role].label}</div>
        </div>
      </aside>
      <main className="min-w-0 flex-1 p-4 md:p-10"><Outlet /></main>
    </div>
    </StaffContext.Provider>
  );
}

export function ScreenHead({ label }: { label: string }) {
  const s = SCREENS.find((x) => x.label === label)!;
  return (
    <div className="mb-6 flex flex-col gap-1">
      <h1 className="text-[30px]">{s.question}</h1>
    </div>
  );
}

export function Loading({ error }: { error?: { message: string } | null }) {
  return error ? <p role="alert" className="text-bad">{error.message}</p> : <p className="text-[15px] text-ink-3">Loading…</p>;
}

export function Tile({ label, value, note, alert }: { label: string; value: string | number; note?: string; alert?: boolean }) {
  return (
    <div className={`rounded-[14px] bg-orbit-2 p-4 ${alert ? "shadow-[inset_0_0_0_1.5px_var(--color-bad)]" : "shadow-[inset_0_0_0_1px_rgb(255_255_255/.08)]"}`}>
      <div className="text-[13px] text-ink-3">{label}</div>
      <div className="font-display text-3xl font-light tabular-nums">{typeof value === "number" ? value.toLocaleString() : value}</div>
      {note && <div className="text-[13px] text-ink-2">{note}</div>}
    </div>
  );
}

export function Table({ children }: { children: React.ReactNode }) {
  return <div className="card overflow-x-auto p-0"><table className="table">{children}</table></div>;
}
